// Cross-sell: griglia compatta di esperienze correlate (card verdi) mostrata in
// fondo alle pagine vetrina per stimolare l'acquisto. Riusa l'API pubblica experiences.
import React from 'react';
import { Link } from 'react-router-dom';
import { mediaUrl } from '@/lib/api';
import { useApiQuery } from '@/hooks/useApiQuery';
import { experienceService } from '@/services/experience.service';
import { Sparkles, ArrowRight, Clock, MapPin } from 'lucide-react';

const euro = (n) => (n == null ? '' : `da €${Number(n).toLocaleString('it-IT', { maximumFractionDigits: 0 })}`);

export const ExperiencesCrossSell = ({
  title = 'Vivi la Sila: esperienze da non perdere',
  subtitle = 'Aggiungi al tuo soggiorno un’esperienza autentica nel Parco.',
  limit = 8,
}) => {
  const { data } = useApiQuery(['public', 'experiences', 'crosssell'], () => experienceService.publicList({ limit }), {
    staleTime: 5 * 60 * 1000,
    select: (d) => (Array.isArray(d) ? d : d?.items || d?.experiences || []),
  });
  const items = (data || []).slice(0, limit);
  if (!items.length) return null;

  return (
    <section className="sila-section py-14" data-testid="experiences-crosssell">
      <div className="flex items-end justify-between gap-4">
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
            <Sparkles className="h-3.5 w-3.5" />Esperienze correlate
          </span>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight">{title}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
        </div>
        <Link to="/esperienze/catalogo" className="hidden shrink-0 items-center gap-1 text-sm font-medium text-emerald-700 hover:underline dark:text-emerald-300 sm:inline-flex" data-testid="crosssell-all">
          Tutte le esperienze <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {items.map((e) => {
          const locName = typeof e.location === 'string' ? e.location : (e.location && e.location.name) || '';
          return (
          <Link
            key={e.id}
            to={`/esperienze/${e.slug || e.id}`}
            className="group flex flex-col overflow-hidden rounded-xl border border-emerald-600/25 bg-emerald-600/[0.06] shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600"
            data-testid={`crosssell-card-${e.id}`}
          >
            <div className="relative aspect-[4/3] overflow-hidden bg-muted">
              {e.cover_image ? (
                <img src={mediaUrl(e.cover_image)} alt={e.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              ) : (
                <div className="flex h-full items-center justify-center text-emerald-700/60"><Sparkles className="h-6 w-6" /></div>
              )}
              {e.from_price != null && (
                <span className="absolute bottom-2 left-2 rounded-full bg-emerald-700 px-2 py-0.5 text-[11px] font-semibold text-white">{euro(e.from_price)}</span>
              )}
            </div>
            <div className="flex flex-1 flex-col p-3">
              <h3 className="line-clamp-2 text-sm font-semibold leading-snug">{e.title}</h3>
              <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-2 text-[11px] text-muted-foreground">
                {locName ? <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{locName}</span> : null}
                {e.duration_min ? <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{Math.round(e.duration_min / 60)}h</span> : null}
              </div>
            </div>
          </Link>
          );
        })}
      </div>
    </section>
  );
};

export default ExperiencesCrossSell;
